import BalanceChange, {
    useBalanceChangeDateRangeState,
} from '@/components/common/BalanceChange';
import { FlexRow } from '@/components/common/Flex';
import { PlentyLogo, Scales01 } from '@/components/common/icons';
import { Skeleton } from '@chakra-ui/react';
import { getNetWorthInfo, getPlentyTotalInfo } from '@common/react/finances';
import {
    useFinancialEntitiesContext,
    useHasNoFEs,
} from '@common/react/financialEntities';
import { usePageContext } from '@common/react/pageMode';
import { isSingleplayer, useCurrentUser } from '@common/react/user';
import { getSampleGoalsTotal } from '@common/sampleData';
import { toDollars } from '@common/util/currency';
import HistoricalBalanceChange from '../common/HistoricalBalanceChange';
import PageTitleSection from '../common/pageLayout/PageTitleSection';
import { PillSwitcher } from '../common/PillSwitcher';
import { Title } from '../common/Typography';
import { useDashboardContext } from './DashboardContext';
import { DashboardChartType } from './helpers';

const DashboardTitleSection = () => {
    const { chartType, setChartType } = useDashboardContext();
    const hasNoFEs = useHasNoFEs();

    return (
        <PageTitleSection
            title={<DashboardTitle chartType={chartType} />}
            rightContent={
                <FlexRow align="end">
                    <PillSwitcher<DashboardChartType>
                        currentValue={hasNoFEs ? 'in-plenty' : chartType}
                        onChange={(value) => setChartType(value)}
                        options={[
                            {
                                value: 'in-plenty',
                                label: <PlentyLogo />,
                                tooltip: '$ in Plenty',
                                tooltipPlacement: 'top',
                            },
                            {
                                value: 'net-worth',
                                label: <Scales01 />,
                                tooltip: 'Net worth',
                                tooltipPlacement: 'top',
                            },
                        ]}
                    />
                </FlexRow>
            }
        />
    );
};

const DashboardTitle = ({
    chartType,
}: {
    chartType: DashboardChartType;
}): JSX.Element => {
    const { pageMode } = usePageContext();
    const { fes } = useFinancialEntitiesContext();
    const hasNoFEs = useHasNoFEs();
    const currentUser = useCurrentUser();
    const { activePartnershipId } = currentUser;
    const [balanceChangeDateRange, setBalanceChangeDateRange] =
        useBalanceChangeDateRangeState();

    if (fes.loading || !fes.data) {
        return <Skeleton h={6} w={80} />;
    }

    const isShared =
        pageMode === 'PUBLIC' &&
        !!activePartnershipId &&
        !isSingleplayer(currentUser);
    const isNetWorthChart = chartType === 'net-worth' && !hasNoFEs;

    let titleStr;
    let balanceChange;

    if (isNetWorthChart) {
        const possessive = isShared ? 'Our' : 'My';
        const info = getNetWorthInfo({
            fesData: fes.data,
        });
        titleStr = `${possessive} net worth is ${toDollars(info.netWorth)}`;

        if (info.change) {
            balanceChange = hasNoFEs ? (
                <BalanceChange
                    dateRange="LAST_30"
                    change={3649}
                    tooltip="Avg. in last 30 days"
                />
            ) : (
                <HistoricalBalanceChange
                    dateRange={balanceChangeDateRange}
                    onChangeDateRange={setBalanceChangeDateRange}
                    positiveAccounts={info.change.positiveAccounts}
                    negativeAccounts={info.change.negativeAccounts}
                    pb={0.5}
                />
            );
        }
    } else {
        const subject = isShared ? 'We' : 'I';
        const info = getPlentyTotalInfo({
            fesData: fes.data,
        });
        const plentyTotal = hasNoFEs ? getSampleGoalsTotal() : info.plentyTotal;
        titleStr = `${subject} have ${toDollars(plentyTotal)} in Plenty`;

        if (info.change) {
            balanceChange = hasNoFEs ? (
                <BalanceChange
                    dateRange="LAST_30"
                    change={3649}
                    tooltip="Avg. in last 30 days"
                />
            ) : (
                <HistoricalBalanceChange
                    dateRange={balanceChangeDateRange}
                    onChangeDateRange={setBalanceChangeDateRange}
                    positiveAccounts={info.change.positiveAccounts}
                    negativeAccounts={info.change.negativeAccounts}
                    pb={0.5}
                />
            );
        }
    }

    return (
        <FlexRow gap={3} align="flex-end">
            <Title>{titleStr}</Title>
            {balanceChange}
        </FlexRow>
    );
};
export default DashboardTitleSection;
